import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchVolunteer, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ApiStatuses } from '../../../global/models/api-model';
import { useApiResponseError } from '../../../global/hooks/useApiError';
import { setClergyBioVolunteer, setLoadingState } from '../clergy-bio-slice';
import { getClergyBioVolunteer, patchClergyBioVolunteer } from '../../../global/ducks/clergy-bio-api';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormTypes } from '../../account-info/models/form-models';
import EcdMessage from '../summary/ecd-message';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ClergyBioVolunteerFormModel } from './models/form-models';
import { ConstructPayload, CreateNewVolunteerItem, GetClergyBioVolunteerValidationSchema } from './clergy-bio-volunteer-helpers';
import ClergyBioVolunteerItem from './clergy-bio-volunteer-item';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';



function ClergyBioVolunteerUpdate() {

	const buttonClasses = buttonStyles();
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.volunteer);
	const volunteer = useSelector<TcsmStates, ClergyBioChurchVolunteer>(state => state?.clergyBioState?.volunteer);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const defaultValues = {
		episcopal: _.cloneDeep(volunteer?.episcopal?.details),
		miscellaneous: _.cloneDeep(volunteer?.miscellaneous?.details),
	};
	const [initialValues, setInitialValues] = useState<ClergyBioVolunteerFormModel>(
		{
			episcopal: [],
			miscellaneous: [],
		},
	);
	const apiErrors = useApiResponseError();

	const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Volunteer, guid);
			setUpdateStatus(ApiStatuses.Success);
		}
		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);
		}
		dispatch(setClergyBioVolunteer(res?.data));
	};

	const updateProfessional = (values: ClergyBioVolunteerFormModel) => {
		dispatch(patchClergyBioVolunteer(guid, ConstructPayload(values), onSuccess));

	};
	const validationSchema = GetClergyBioVolunteerValidationSchema(settings, labelSettings);

	const addNewItem = (key: string) => {
		const updated = initialValues[key];
		updated.push(CreateNewVolunteerItem());
		const copy = { ...initialValues };
		copy[key] = updated;
		setInitialValues(copy);
	};

	useEffect(() => {

		setInitialValues(
			{
				episcopal: _.cloneDeep(volunteer?.episcopal?.details),
				miscellaneous: _.cloneDeep(volunteer?.miscellaneous?.details),
			},
		);

	}, [volunteer]);

	useEffect(() => {

		return function cleanUp() {
			dispatch(setLoadingState(DataState.LOADING, 'volunteer'));
			dispatch(getClergyBioVolunteer(
				guid,
				(data) => dispatch(setClergyBioVolunteer(data?.data)),
				() => dispatch(setLoadingState(DataState.ERROR, 'volunteer'))),
			);
		};
	}, []);

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateProfessional(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<ClergyBioVolunteerFormModel>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								setUpdateStatus(ApiStatuses.None);
								setErrorMessage(null);
								dispatch(setLoadingState(DataState.LOADING, 'volunteer'));
								dispatch(getClergyBioVolunteer(
									guid,
									(data) => dispatch(setClergyBioVolunteer(data?.data)),
									() => dispatch(setLoadingState(DataState.ERROR, 'volunteer'))),
								);
								window.scrollTo(0, 0);
							}}
							initialValue={defaultValues}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_PROFESSIONAL_UPDATE] : []}
						>
							<PageHeader headerText={settings?.volunteerTitle}  />
							
							<ContentSpacer>
								<EcdMessage />
							</ContentSpacer>
							{
								updateStatus === ApiStatuses.Failure &&
								<ContentSpacer>
									<Banner
										banner={{
											type: 'error',
											label: <p><strong>{errorMessage}</strong></p>,
										}}
									/>
								</ContentSpacer>
							}
							<DividerSection sectionText={settings?.episcopalVolunteerTitle} sectionDescription={settings?.episcopalUpdateDescription} showCleryIcon>
								{
									initialValues?.episcopal?.map((p, idx) =>
										<ClergyBioVolunteerItem
											key={`episcopal-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='episcopal'
											formikProps={formProps}
											volunteerItem={p}
											setList={setInitialValues}
											defaultValue={defaultValues?.episcopal[idx]}
										/>)
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewItem('episcopal')} >{settings?.episcopalVolunteerAddButtonText}</button>
							</DividerSection>
							<DividerSection removeDivider sectionText={settings?.miscellaneousVolunteerTitle} sectionDescription={settings?.miscellaneousVolunteerUpdateDescription} showCleryIcon>
								{
									initialValues?.miscellaneous?.map((p, idx) =>
										<ClergyBioVolunteerItem
											key={`miscellaneous-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='miscellaneous'
											formikProps={formProps}
											volunteerItem={p}
											setList={setInitialValues}
											defaultValue={defaultValues?.miscellaneous[idx]}
											miscellaneous
										/>)
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewItem('miscellaneous')} >{settings?.miscellaneousVolunteerAddButtonText}</button>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<ClergyBioComponentLoader
			state={loadingState}
			content={renderContent()}
		/>
	);
}


export default ClergyBioVolunteerUpdate;